import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, ChevronLeft, ChevronRight, Loader2, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AccuracyRules from "./AccuracyRules";

const ALL = "__all";

function Pager({ page, pages, total, pageSize, onPage, where }) {
  if (pages < 2) return null;
  return (
    <div className="flex items-center justify-between gap-3" data-testid={`review-queue-pagination-${where}`}>
      <Button variant="outline" size="sm" className="rounded-full" disabled={page <= 0} onClick={() => onPage(page - 1)} data-testid={`review-queue-prev-page-${where}`}><ChevronLeft className="mr-1 h-4 w-4" /> Previous</Button>
      <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground" data-testid={`review-queue-page-${where}`}>Page {page + 1} of {pages} · {page * pageSize + 1}–{Math.min(total, (page + 1) * pageSize)} of {total}</span>
      <Button variant="outline" size="sm" className="rounded-full" disabled={page >= pages - 1} onClick={() => onPage(page + 1)} data-testid={`review-queue-next-page-${where}`}>Next <ChevronRight className="ml-1 h-4 w-4" /></Button>
    </div>
  );
}

/* Landing view of the Review queue: collapsible accuracy rules + the drafts waiting, one click opens a question. */
export function ReviewQueueList({ drafts, total, page, pageSize, onPage, onOpen, books, view, book, onFilter }) {
  const qc = useQueryClient();
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const filtered = !!(view || book);
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(null);
  const onPageIds = drafts.map((q) => q.id);
  const allOnPage = onPageIds.length > 0 && onPageIds.every((id) => selected.has(id));
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const togglePage = () => setSelected((s) => { const n = new Set(s); allOnPage ? onPageIds.forEach((id) => n.delete(id)) : onPageIds.forEach((id) => n.add(id)); return n; });
  const bulk = async (action) => {
    const ids = [...selected];
    if (action === "delete" && !window.confirm(`Delete ${ids.length} draft${ids.length === 1 ? "" : "s"} permanently? This cannot be undone.`)) return;
    setBusy(action);
    try {
      const { data } = await api.post("/admin/questions/bulk", { ids, action });
      toast.success(action === "publish" ? `Published ${data.count} question${data.count === 1 ? "" : "s"}.` : `Deleted ${data.count} draft${data.count === 1 ? "" : "s"}.`);
      setSelected(new Set());
      ["admin-questions", "admin-overview", "questions", "types"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };
  return (
    <div className="space-y-4" data-testid="review-queue">
      <AccuracyRules />
      <section className="rounded-2xl border border-border bg-card" data-testid="review-queue-list">
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
          <div>
            <h2 className="font-serif text-lg font-semibold text-foreground">Questions needing review</h2>
            <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground" data-testid="review-queue-count">{total} draft{total === 1 ? "" : "s"}{filtered ? " match" : ""}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={view || ALL} onValueChange={(v) => onFilter({ view: v === ALL ? "" : v })}>
              <SelectTrigger className="h-9 w-40 rounded-full" data-testid="review-queue-filter-testament"><SelectValue placeholder="Testament" /></SelectTrigger>
              <SelectContent><SelectItem value={ALL}>Both testaments</SelectItem><SelectItem value="ot">Old Testament</SelectItem><SelectItem value="nt">New Testament</SelectItem></SelectContent>
            </Select>
            <Select value={book || ALL} onValueChange={(v) => onFilter({ book: v === ALL ? "" : v })}>
              <SelectTrigger className="h-9 w-44 rounded-full" data-testid="review-queue-filter-book"><SelectValue placeholder="Book" /></SelectTrigger>
              <SelectContent className="max-h-80"><SelectItem value={ALL}>All books</SelectItem>{books.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
            </Select>
            {filtered && <Button variant="ghost" size="sm" className="rounded-full" onClick={() => onFilter({ view: "", book: "" })} data-testid="review-queue-clear-filters">Clear</Button>}
          </div>
        </header>
        {pages > 1 && <div className="border-b border-border px-5 py-3"><Pager page={page} pages={pages} total={total} pageSize={pageSize} onPage={onPage} where="top" /></div>}
        {!total ? (
          <div className="p-10 text-center" data-testid="review-empty">
            <p className="font-serif text-2xl text-foreground">{filtered ? "No drafts match these filters" : "No drafts waiting"}</p>
            <p className="mt-2 text-sm text-muted-foreground">{filtered ? "Try another book or testament, or clear the filters." : "Imported or bulk-added drafts will queue up here. To review a published question, open the Questions tab and use its shield button."}</p>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            <li className="flex items-center gap-4 bg-muted/30 px-5 py-2">
              <Checkbox checked={allOnPage} onCheckedChange={togglePage} aria-label="Select all on this page" data-testid="review-queue-select-page" />
              <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{selected.size ? `${selected.size} selected` : "Select all on this page"}</span>
            </li>
            {drafts.map((q) => (
              <li key={q.id} className={`flex items-center gap-4 px-5 transition-colors hover:bg-muted/60 ${selected.has(q.id) ? "bg-gold/5" : ""}`}>
                <Checkbox checked={selected.has(q.id)} onCheckedChange={() => toggle(q.id)} aria-label={`Select ${q.reference}`} data-testid={`review-queue-select-${q.id}`} />
                <button type="button" onClick={() => onOpen(q.id)} className="flex min-w-0 flex-1 items-center gap-4 py-3 text-left" data-testid={`review-queue-item-${q.id}`}>
                  <span className="w-36 shrink-0 font-mono text-xs text-gold">{q.reference}</span>
                  <span className="min-w-0 flex-1 truncate font-serif text-sm text-foreground">{q.question_kjv}</span>
                  <span className="hidden shrink-0 font-mono text-[10px] uppercase tracking-wider text-muted-foreground sm:inline">{q.testament} · {q.who_group}</span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                </button>
              </li>
            ))}
          </ul>
        )}
        {pages > 1 && <div className="border-t border-border px-5 py-3"><Pager page={page} pages={pages} total={total} pageSize={pageSize} onPage={onPage} where="bottom" /></div>}
      </section>
      {selected.size > 0 && (
        <div className="sticky bottom-4 z-20 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-background/95 p-4 shadow-lg backdrop-blur" data-testid="review-bulk-bar">
          <div className="flex items-center gap-3">
            <span className="font-mono text-xs text-foreground" data-testid="review-bulk-count">{selected.size} draft{selected.size === 1 ? "" : "s"} selected</span>
            <button type="button" onClick={() => setSelected(new Set())} className="text-xs text-muted-foreground hover:text-foreground" data-testid="review-bulk-clear">Clear selection</button>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" disabled={!!busy} onClick={() => bulk("delete")} className="rounded-full text-destructive hover:text-destructive" data-testid="review-bulk-delete">{busy === "delete" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />} Delete selected</Button>
            <Button disabled={!!busy} onClick={() => bulk("publish")} className="rounded-full bg-primary text-primary-foreground" data-testid="review-bulk-publish">{busy === "publish" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />} Approve &amp; publish selected</Button>
          </div>
        </div>
      )}
    </div>
  );
}
